/**
 * Pure Email Validation Utility
 * 
 * Environment-agnostic email validation that works in:
 * - Browser (frontend React components)
 * - Node.js (Netlify functions)
 * - Any JavaScript environment
 * 
 * No external dependencies - pure validation logic only.
 */

/**
 * Validates email address using enhanced regex pattern and additional checks
 * @param {string} email - Email address to validate
 * @returns {string|null} - Normalized email if valid, null if invalid
 */
export const validateEmail = email => {
  if (!email || typeof email !== 'string') {
    return null;
  }

  // Enhanced regex pattern that:
  // - Allows letters, numbers, dots, underscores, percent, plus, hyphen in local part
  // - Requires proper @ symbol
  // - Validates domain with letters, numbers, dots, hyphens
  // - Ensures TLD is at least 2 characters and only letters
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  // Additional checks for edge cases
  const trimmedEmail = email.trim().toLowerCase();

  // Check basic format
  if (!emailRegex.test(trimmedEmail)) {
    return null;
  }

  // Prevent consecutive dots in local part
  if (trimmedEmail.includes('..')) {
    return null;
  }

  // Prevent starting or ending with dot in local part
  const localPart = trimmedEmail.split('@')[0];
  if (localPart.startsWith('.') || localPart.endsWith('.')) {
    return null;
  }

  // Basic length check (RFC 5321 limit)
  if (trimmedEmail.length > 254) {
    return null;
  }

  return trimmedEmail;
};

/**
 * Validates email with detailed error information
 * @param {string} email - Email address to validate
 * @returns {object} - Object with isValid boolean, error message, and normalized email
 */
export const validateEmailWithDetails = email => {
  const validatedEmail = validateEmail(email);
  
  if (validatedEmail) {
    return {
      isValid: true,
      error: null,
      email: validatedEmail,
    };
  }

  // Provide specific error messages for common issues
  if (!email || typeof email !== 'string') {
    return {
      isValid: false,
      error: 'Email is required and must be a string',
      email: null,
    };
  }

  const trimmedEmail = email.trim();

  if (trimmedEmail.length === 0) {
    return {
      isValid: false,
      error: 'Email cannot be empty',
      email: null,
    };
  }

  if (trimmedEmail.length > 254) {
    return {
      isValid: false,
      error: 'Email is too long (maximum 254 characters)',
      email: null,
    };
  }

  if (trimmedEmail.includes('..')) {
    return {
      isValid: false,
      error: 'Email cannot contain consecutive dots',
      email: null,
    };
  }

  const localPart = trimmedEmail.split('@')[0];
  if (localPart.startsWith('.') || localPart.endsWith('.')) {
    return {
      isValid: false,
      error: 'Email cannot start or end with a dot',
      email: null,
    };
  }

  // Generic error for other validation failures
  return {
    isValid: false,
    error: 'Please enter a valid email address',
    email: null,
  };
};

// CommonJS export for Node.js compatibility (Netlify functions)
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    validateEmail,
    validateEmailWithDetails,
  };
}
